import React, { useState, useEffect, useContext } from "react";
import ReusableNivoBarChart from "../../components/ReusableNivoBarChart";
import ClusterParticipationChart from "../../components/ClusterParticipationChart";
import { ProductsContext } from "../../context/products.context";
import SummaryControls, { YearSelect, DenominatorToggle } from "../../components/SummaryControls";


function getSchoolTotalStudents(summaryData, schoolName) {
  const groupData = summaryData.schools[schoolName]["All Students"];
  if (!groupData || !groupData.totalStudents) {
    return 0;
  }

  
  // sum up all the "positive" numbers
  const sum = groupData.totalStudents.reduce((acc, val) => {
    if (typeof val === "number" && val > 0) {
      return acc + val;
    }
    return acc;
  }, 0);

  // Now log only once
  
  return sum;
}


function safeArray(arr) {
  return Array.isArray(arr) ? arr : [];
}


/**
 * Safely compute the percentage or return a negative sentinel if masked.
 * If 'numerator' is negative => masked. If 'denominator' is negative => masked.
 * If 'denominator' is 0 or null => no data, etc.
 */
function computePercent(numerator, denominator) {
  // If denominator is negative => use that code
  if (typeof denominator === 'number' && denominator < 0) {
    return denominator; 
  }
  // else if numerator is negative => use that
  if (typeof numerator === 'number' && numerator < 0) {
    return numerator; 
  }

  // else if denominator == 0 or null => no data
  if (denominator == null || denominator <= 0) {
    return null; 
  }
  // if numerator == null => no data
  if (numerator == null) {
    return null;
  }
  // normal
  return (numerator / denominator) * 100;
}


const Summary = () => {

  
  const { summaryData } = useContext(ProductsContext);
  const [selectedComparisonGroup, setSelectedComparisonGroup] = useState("none"); // Default to "none"
  // State for selections
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedYear, setSelectedYear] = useState(2024);
  const [sortedSchoolKeys, setSortedSchoolKeys] = useState(null);
  
  const [defaulting, setDefaulting] = useState(false);
  const [defaultApplied, setDefaultApplied] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState("All Students");
  const [denominatorOption, setDenominatorOption] = useState("totalStudents");
  // ------------------------------------------
  // 1) ALWAYS define your hooks unconditionally:
  // ------------------------------------------
  useEffect(() => {
    if (!summaryData) {
      return; // Wait until data is available
    }
  
    // Get the schools object
    const schoolsObj = summaryData.schools;
    if (!schoolsObj) {
      setSortedSchoolKeys([]);
      return;
    }
  
    // Sort school keys by total students
    const schoolKeys = Object.keys(schoolsObj).sort((a, b) => {
      const totalA = getSchoolTotalStudents(summaryData, a);
      const totalB = getSchoolTotalStudents(summaryData, b);
      return totalB - totalA; // Sort descending
    });
  
    setSortedSchoolKeys(schoolKeys);
  
    // Check if selectedSchool exists in the updated schools list
    if (!schoolsObj[selectedSchool]) {
      const defaultSchool = schoolKeys[0]; // Pick the first school as default
      setSelectedSchool(defaultSchool);
      setDefaultApplied(true); // Ensure this logic doesn't re-run unnecessarily
      // Defaulting to first school
    }
  }, [summaryData, selectedSchool]);
  
  
  // ------------------------------------------
  // 2) Then do any early returns for loading or missing data:
  // ------------------------------------------
  if (!summaryData) {
    return <p>Loading data from Firestore...</p>;
  }

  if (!sortedSchoolKeys) {
    return <p>Sorting schools...</p>;
  }
  if (sortedSchoolKeys.length === 0) {
    return <p>No schools found in summary data.</p>;
  }

  // If no selectedSchool yet, pick the first
  if (!selectedSchool) {
    setSelectedSchool(sortedSchoolKeys[0]);
    return <p>Picking default: {sortedSchoolKeys[0]}...</p>;
  }


  // 6) If user picked a school that doesn't exist => show fallback
  if (!summaryData.schools[selectedSchool]) {
  if (!defaulting) {
    setDefaulting(true);
    const defaultSchool = sortedSchoolKeys[0];
    setSelectedSchool(defaultSchool);
  }
  return (
    <p className="p-4 text-blue-500">
      Selected school was not found. Defaulting to: {sortedSchoolKeys[0]}.
    </p>
  );
}


  // 5) Now safely reference summaryData
  const schoolData = summaryData.schools[selectedSchool]?.[selectedGroup] || {};


  const comparisonData = summaryData.schools[selectedSchool]?.[selectedComparisonGroup] || {};
  const yearIndex = Math.max(0, Math.min(4, selectedYear - 2020)); // Ensure yearIndex is between 0 and 4

  // --- Denominator toggle ("Per Total Students" vs "Per CTE Participants")
  // Only the Concentrator Trends chart honors the toggle:
  //  - Enrollment Trends is participants / total students by definition.
  //  - Cluster charts are always a share of CTE participants.
  const denomKey = denominatorOption === "totalParticipants" ? "totalParticipants" : "totalStudents";
  const denomLabel = denomKey === "totalParticipants" ? "CTE participants" : "students";
  const denomTitle = denomKey === "totalParticipants" ? "% of CTE participants" : "% of all students";

  const clusterObj = schoolData.clusters || {};
  const groupData = (groupName) => summaryData.schools[selectedSchool]?.[groupName] || {};

  // Build one row per cluster for a given group and numerator field (always per CTE participants).
  const buildClusterRows = (groupName, field) => {
    const data = groupData(groupName);
    const denom = safeArray(data.totalParticipants)[yearIndex] ?? null;
    return Object.keys(clusterObj).map((clusterName) => {
      const arr = data.clusters?.[clusterName]?.[field];
      const val = Array.isArray(arr) && arr.length > yearIndex ? arr[yearIndex] : null;
      return {
        clusterName,
        "Cluster Participation %": computePercent(val, denom),
        numerator: val,
        denominator: denom,
        denominatorLabel: "CTE participants",
        year: selectedYear,
        studentGroup: groupName,
      };
    });
  };

  const clusterData = buildClusterRows(selectedGroup, "enrolled");
  const concData = buildClusterRows(selectedGroup, "concentrators");
  const comparisonClusterData =
    selectedComparisonGroup === "none" ? [] : buildClusterRows(selectedComparisonGroup, "enrolled");
  const comparisonClusterConcentratorData =
    selectedComparisonGroup === "none" ? [] : buildClusterRows(selectedComparisonGroup, "concentrators");

  // Comparison-group arrays for the year-based charts
  const safeComparisonStudents = safeArray(comparisonData?.totalStudents);
  const safeComparisonParticipants = safeArray(comparisonData?.totalParticipants);

  // --- Year-based bar charts
// For Enrollment Data:
const enrollmentData = safeArray(schoolData.totalStudents).map((total, index) => {
  const participants = safeArray(schoolData.totalParticipants)[index];
  return {
    year: 2020 + index,
    "Enrollment %": computePercent(participants, total),
    numerator: participants,
    denominator: total,
    denominatorLabel: "students",
    studentGroup: selectedGroup,
  };
});

  const chartEnrollmentData = enrollmentData.map((item) => ({
    year: item.year.toString(),
    "Enrollment %": item["Enrollment %"],
    numerator: item.numerator,
    denominator: item.denominator,
    denominatorLabel: item.denominatorLabel,
    studentGroup: item.studentGroup,
  }));

    // --- Year-based bar charts for comparison group
// For the .map() usage:
const comparisonEnrollmentData = safeComparisonStudents.map((total, index) => {
  const comparisonParticipants = safeComparisonParticipants[index] ?? null;
  const pctVal = computePercent(comparisonParticipants, total);
  return {
    year: 2020 + index,
    [`${selectedComparisonGroup} Enrollment %`]: pctVal,
    numerator: comparisonParticipants,
    denominator: total,
    denominatorLabel: "students",
    studentGroup: selectedComparisonGroup,
  };
});

const chartComparisonEnrollmentData = comparisonEnrollmentData.map((item) => ({
  year: item.year.toString(),
  [`${selectedComparisonGroup} Enrollment %`]: item[`${selectedComparisonGroup} Enrollment %`],
  numerator: item.numerator,
  denominator: item.denominator,
  denominatorLabel: item.denominatorLabel,
  studentGroup: item.studentGroup,
}));

  // --- Year-based bar charts for concentrator data
// For Concentrator Data (denominator follows the toggle):
const concentratorData = safeArray(schoolData[denomKey]).map((total, index) => {
  const concentrators = safeArray(schoolData.totalConcentrators)[index];
  return {
    year: 2020 + index,
    "Concentrator %": computePercent(concentrators, total),
    numerator: concentrators,
    denominator: total,
    denominatorLabel: denomLabel,
    studentGroup: selectedGroup,
  };
});

const chartConcentratorData = concentratorData.map((item) => ({
  year: item.year.toString(),
  "Concentrator %": item["Concentrator %"],
  numerator: item.numerator,
  denominator: item.denominator,
  denominatorLabel: item.denominatorLabel,
  studentGroup: item.studentGroup,
}));

// --- Year-based bar charts for comparison group (denominator follows the toggle)
const safeComparisonDenom = safeArray(comparisonData?.[denomKey]);
const safeComparisonConcentrators = safeArray(comparisonData?.totalConcentrators);
const comparisonConcentratorData = safeComparisonDenom.map((total, index) => {
  const comparisonConcentrators = safeComparisonConcentrators[index] ?? null;
  const pctVal = computePercent(comparisonConcentrators, total);
  return {
    year: 2020 + index,
    [`${selectedComparisonGroup} Concentrator %`]: pctVal,
    numerator: comparisonConcentrators,
    denominator: total,
    denominatorLabel: denomLabel,
    studentGroup: selectedComparisonGroup,
  };
});

const chartComparisonConcentratorData = comparisonConcentratorData.map((item) => ({
  year: item.year.toString(),
  [`${selectedComparisonGroup} Concentrator %`]: item[`${selectedComparisonGroup} Concentrator %`],
  numerator: item.numerator,
  denominator: item.denominator,
  denominatorLabel: item.denominatorLabel,
  studentGroup: item.studentGroup,
}));


  // 6) Render actual page
  return (
    <div className="bg-zinc-100 min-h-full">
      {/* Global controls: apply to every chart below */}
      <SummaryControls
        schools={sortedSchoolKeys}
        selectedSchool={selectedSchool}
        onSchoolChange={setSelectedSchool}
        selectedGroup={selectedGroup}
        onGroupChange={setSelectedGroup}
        selectedComparisonGroup={selectedComparisonGroup}
        onComparisonChange={setSelectedComparisonGroup}
      />

      <div className="max-w-[1200px] mx-auto p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">Summary View</h1>
          <p className="text-gray-600">
            Enrollment and participation for {selectedSchool}, {selectedGroup}
            {selectedComparisonGroup !== "none" ? ` compared to ${selectedComparisonGroup}` : ""}.
          </p>
        </header>

        {/* Trends: all years */}
        <section>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Trends, 2020&ndash;2024</h2>
            <p className="text-sm text-gray-500">
              Share of students enrolled in any CTE course, and share who concentrated.
            </p>
          </div>

          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 w-full bg-white p-8 rounded-lg shadow-md">
            <div className="pr-6">
              <ReusableNivoBarChart
                data={chartEnrollmentData}
                theme={{ primary: "clusterChartBlue", comparison: "clusterChartOrange" }}
                keys={["Enrollment %"]}
                colors={{ "Enrollment %": "#A3CAFA" }}
                titleText={`Enrollment Trends for ${selectedSchool}`}
                indexBy="year"
                chartComparisonEnrollmentData={chartComparisonEnrollmentData}
                selectedComparisonGroup={selectedComparisonGroup}
                comparisonColor="#F58C6C"
                selectedStudentGroup={selectedGroup}
              />
            </div>

            <div className="pr-6">
              <div className="flex justify-end">
                <DenominatorToggle value={denominatorOption} onChange={setDenominatorOption} />
              </div>
              <ReusableNivoBarChart
                data={chartConcentratorData}
                theme={{ primary: "clusterChartBlue", comparison: "clusterChartOrange" }}
                keys={["Concentrator %"]}
                colors={{ "Concentrator %": "#51DDA8" }}
                titleText={`Concentrator Trends for ${selectedSchool} (${denomTitle})`}
                indexBy="year"
                chartComparisonEnrollmentData={chartComparisonConcentratorData}
                selectedComparisonGroup={selectedComparisonGroup}
                comparisonColor="#F58C6C"
                selectedStudentGroup={selectedGroup}
              />
            </div>
          </div>
        </section>

        {/* Clusters: one year at a time */}
        <section className="mt-12">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Career Clusters, {selectedYear}</h2>
              <p className="text-sm text-gray-500">
                Each cluster as a share of CTE participants in the selected year.
              </p>
            </div>
            <YearSelect selectedYear={selectedYear} onYearChange={setSelectedYear} startYear={2020} yearCount={5} />
          </div>

          <div className="grid gap-6 grid-cols-1">
            <div className="bg-white shadow-md rounded-lg p-6">
              <ClusterParticipationChart
                clusterData={clusterData}
                primaryGroup={selectedGroup}
                comparisonGroup={selectedComparisonGroup}
                theme={{ primary: "clusterChartBlue", comparison: "clusterChartOrange" }}
                keys={["Cluster Participation %"]}
                titleText={`Cluster Participation for ${selectedSchool} - ${selectedYear} (% of CTE participants)`}
                comparisonData={comparisonClusterData}
                color="#A3CAFA"
                comparisonColor="#F58C6C"
              />
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
              <ClusterParticipationChart
                clusterData={concData}
                primaryGroup={selectedGroup}
                comparisonGroup={selectedComparisonGroup}
                theme={{ primary: "clusterChartBlue", comparison: "clusterChartOrange" }}
                keys={["Cluster Participation %"]}
                titleText={`Cluster Concentration for ${selectedSchool} - ${selectedYear} (% of CTE participants)`}
                comparisonData={comparisonClusterConcentratorData}
                color="#51DDA8"
                comparisonColor="#F58C6C"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Summary;
